<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TaccRelatedContactsAPI from 'dashboard/api/taccRelatedContacts';
import AddRelatedContactModal from './AddRelatedContactModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const isLoading = ref(false);
const error = ref('');
const company = ref(null);
const contacts = ref([]);
const showAddModal = ref(false);

const hasCompany = computed(() => !!company.value?.id);
const contactProfilePath = contact =>
  `/app/accounts/${store.getters.getCurrentAccountId}/contacts/${contact.id}`;

const refresh = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await TaccRelatedContactsAPI.getRelatedContacts(
      props.conversationId
    );
    company.value = data.company || null;
    contacts.value = data.contacts || [];
  } catch (err) {
    error.value =
      err?.response?.data?.error || err.message || 'Could not load contacts.';
    contacts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const createContact = async payload => {
  await store.dispatch('contacts/create', payload);
  showAddModal.value = false;
  await refresh();
};

const onCompanyUpdated = () => refresh();

watch(() => props.conversationId, refresh);
onMounted(() => {
  refresh();
  window.addEventListener('tacc:company-updated', onCompanyUpdated);
});
onUnmounted(() => {
  window.removeEventListener('tacc:company-updated', onCompanyUpdated);
});
</script>

<template>
  <div class="px-4 py-4">
    <div v-if="isLoading" class="py-6 text-center text-sm text-n-slate-10">
      Loading contacts...
    </div>
    <div v-else-if="error" class="py-6 text-center text-sm text-n-ruby-10">
      {{ error }}
    </div>
    <div v-else-if="!contacts.length" class="flex flex-col items-center gap-3 py-7 text-center">
      <span class="i-lucide-users-round size-7 text-n-blue-10" />
      <div>
        <p class="m-0 text-sm font-medium text-n-slate-12">
          No related contacts yet
        </p>
        <p class="m-0 mt-1 text-xs leading-5 text-n-slate-10">
          {{
            hasCompany
              ? 'Add colleagues from the same company.'
              : 'Select a company on this contact before adding related contacts.'
          }}
        </p>
      </div>
      <NextButton
        blue
        sm
        label="Add contact"
        :disabled="!hasCompany"
        @click="showAddModal = true"
      />
    </div>
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="contact in contacts"
        :key="contact.id"
        class="flex items-center gap-3 rounded-lg bg-n-alpha-1 p-3"
      >
        <div
          class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-blue-3 text-xs font-semibold uppercase text-n-blue-10"
        >
          {{ (contact.name || '?').slice(0, 2) }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="m-0 truncate text-sm font-medium text-n-slate-12">
            {{ contact.name }}
          </p>
          <p class="m-0 truncate text-xs text-n-slate-10">
            {{ contact.email || contact.phone_number || company?.name }}
          </p>
        </div>
        <a
          :href="contactProfilePath(contact)"
          target="_blank"
          rel="noopener noreferrer"
          class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        >
          <span class="i-lucide-external-link text-sm text-n-slate-10" />
        </a>
      </div>
      <NextButton
        faded
        blue
        sm
        icon="i-lucide-plus"
        label="Add contact"
        @click="showAddModal = true"
      />
    </div>
    <AddRelatedContactModal
      :show="showAddModal"
      :company="company"
      :on-create="createContact"
      @close="showAddModal = false"
    />
  </div>
</template>
